import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Linkedin, Twitter, Mail } from 'lucide-react';
import api from '../../../services/api';
import { getSubdomainUsername, getUsernameSyncOnly } from '../../../utils/subdomain';
import { buildMediaUrl } from '../../../utils/mediaUrl';

const PublicTeam = () => {
  const { username: urlUsername } = useParams();
  const subdomainUser = getSubdomainUsername();
  const cachedCustomDomainUser = getUsernameSyncOnly();
  const username = urlUsername || subdomainUser || cachedCustomDomainUser;

  const [data, setData] = useState({ section: null, members: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get(`/business/team/public/${username}/`);
        // API returns array directly
        const members = Array.isArray(res.data) ? res.data : (res.data.members || res.data.items || []);
        setData({
          section: res.data.section || {},
          members: members
        });
      } catch (err) {
        console.error('Error fetching team:', err);
      } finally {
        setLoading(false);
      }
    };
    if (username) fetchData();
  }, [username]);

  if (loading) {
    return (
      <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
        <div style={{
          width: '2rem', height: '2rem',
          border: '3px solid rgba(255,255,255,0.1)',
          borderTopColor: '#6366f1',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto'
        }} />
      </div>
    );
  }

  if (!data.members.length) {
    return (
      <div id="section-team" style={{ padding: '4rem 2rem', textAlign: 'center', backgroundColor: '#0f172a' }}>
        <p style={{ color: 'rgba(255,255,255,0.5)' }}>No team members available yet.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '4rem 2rem', backgroundColor: '#0f172a' }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
          <h2 style={{ fontSize: '2.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.5rem' }}>
            {data.section?.title || 'Our Team'}
          </h2>
          {data.section?.subtitle && (
            <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '1.1rem' }}>
              {data.section.subtitle}
            </p>
          )}
        </div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '2rem'
        }}>
          {data.members.map((member, index) => (
            <div
              key={member.id || index}
              style={{
                padding: '2rem',
                backgroundColor: 'rgba(255,255,255,0.05)',
                borderRadius: '1rem',
                textAlign: 'center'
              }}
            >
              <div style={{
                width: '120px',
                height: '120px',
                borderRadius: '50%',
                overflow: 'hidden',
                margin: '0 auto 1rem',
                border: '3px solid rgba(99, 102, 241, 0.3)'
              }}>
                {member.photo || member.image ? (
                  <img
                    src={buildMediaUrl(member.photo || member.image)}
                    alt={member.name}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#334155',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#94a3b8',
                    fontSize: '2.5rem',
                    fontWeight: '600'
                  }}>
                    {member.name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}
              </div>

              <h3 style={{ color: '#fff', fontSize: '1.25rem', fontWeight: '600', marginBottom: '0.25rem' }}>
                {member.name}
              </h3>
              {member.role && (
                <p style={{ color: '#6366f1', fontSize: '0.9rem', marginBottom: '0.75rem' }}>
                  {member.role}
                </p>
              )}
              {member.bio && (
                <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', lineHeight: '1.5' }}>
                  {member.bio}
                </p>
              )}

              {/* Social Links */}
              {(member.linkedin || member.twitter || member.email) && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '0.75rem', marginTop: '1rem' }}>
                  {member.linkedin && (
                    <a href={member.linkedin} target="_blank" rel="noopener noreferrer"
                       style={{ color: '#6366f1', transition: 'color 0.2s' }}>
                      <Linkedin size={20} />
                    </a>
                  )}
                  {member.twitter && (
                    <a href={member.twitter} target="_blank" rel="noopener noreferrer"
                       style={{ color: '#6366f1', transition: 'color 0.2s' }}>
                      <Twitter size={20} />
                    </a>
                  )}
                  {member.email && (
                    <a href={`mailto:${member.email}`}
                       style={{ color: '#6366f1', transition: 'color 0.2s' }}>
                      <Mail size={20} />
                    </a>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default PublicTeam;
